/**
Copyright (C) 2018 Andras Sarvari <andras.sarvari@sarvaria.net>

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted, provided that the above
copyright notice and this permission notice appear in all copies.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
*/

/* jshint browser: true, esversion: 6, node:true */
/* globals console */

import React from 'react';
import PropTypes from 'prop-types';
import Control from '../Control/Control';
import Label from '../Label/Label';
import './Textarea.css';

/**
 * Represents the textarea component.
 */

export default class Textarea extends Control {

    /** Label positions. */
    static get LABELPOSITION() { return {
        TOP:   'top',
        LEFT:  'left'
    };}

    /**
     * Properties.
     */

    static propTypes = {
        label:            PropTypes.string,
        labelwidth:       PropTypes.number,
        labelposition:    PropTypes.string,
        maxlength:        PropTypes.number,
        disabled:         PropTypes.bool,
        touppercase:      PropTypes.bool,
        tocapitalize:     PropTypes.bool,
        isrequired:       PropTypes.bool,
        validateontyping: PropTypes.bool,
        validateonblur:   PropTypes.bool,
        requiredmessage:  PropTypes.string,
        placeholder:      PropTypes.string,
        tooltip:          PropTypes.string,
        width:            PropTypes.number,
        height:           PropTypes.number,
        comment:          PropTypes.string
    }

    /**
     * Default property values.
     */

    static defaultProps = {
        requiredmessage:    'Mandatory field'
    }

    /**
     * Default state.
     */

    state = { 
        error:              null, 
        showerror:          false
    }
   
    /**
     * Sets the focus to the textarea.
     */
    
    setFocus = () => {
        this.input.focus();
    }

    /**
     * Gets the value of the textarea.
     *
     * @returns {String}  value   value of the textarea
     */

    getValue = () => {
      
        // Getting the value of the textarea

        return this.props.touppercase ? this.input.value.toUpperCase() : this.input.value;
    }

    /**
     * Sets the value of the textarea.
     *
     * @param {String}  value           value of the textarea
     * @param {Boolean} novalidation    no validate during the value set
     */

    setValue = (value, novalidation) => {
      
        // Setting the value of the textarea

        this.input.value = value;

        if (!novalidation) {
            this.validate();
        }
    }

    /**
     * Clears the value and the error message of the textarea.
     */

    clearValue = () => {
      
        // Clearing the value

        this.input.value = '';
        this.clearError();
    }

    /**
     * Clears the error message of the textarea.
     */

    clearError = () => {
      
        // Clearing the error

        this.setError();
    }

    /**
     * Sets the error message of the textarea.
     *
     * @param {String}  message   error message that has to show
     */

    setError = (message) => {
      
        // Does the error message specified?

        if (message) {

            // Showing the error message

            this.setState({ error: message }, () => {
                setTimeout(() => {
                    this.setState({ showerror: true });
                }, 20);
            });
        } else {

            // Clearing the error message

            this.setState({ showerror: false }, () => {
                setTimeout(() => {
                    this.setState({ error: null });
                }, 250);
            });
        }
    }

    /**
     * Checks that the value of the textarea is empty or not.
     *
     * @param     {String}  value   value that has to check (optional)
     * @returns   {Boolean} empty   specified value or the value of the textarea is
     *                              empty or not
     */

    isEmpty = (value) => {
        
        // Getting the value

        let v = (value || this.getValue());

        // Checking the value

        return (v === null || v === undefined) ? true : v.toString().trim() === '';
    }

    /**
     * Validates the textarea.
     * 
     * @param     {String}  value   value that has to check (optional)
     * @returns   {Boolean} valid   specified value or the value of the textarea is
     *                              valid or not
     */

    validate = (value) => {

        // Does the value of the textarea valid?

        if (!(this.props.isrequired && this.isEmpty(value))) {
            if (!value) {
                this.clearError();
            }

            return true;
        }

        // Textarea is invalid

        this.setError(this.props.requiredmessage);
        return false;
    }

    /**
     * Validates the textarea on typing.
     */

    _validateOnTyping = () => {
        if (this.props.validateontyping) {
            this.validate();
        }
    }

    /**
     * Validates the textarea on blur.
     */

    _validateOnBlur = () => {
        if (this.props.validateonblur) {
            this.validate();
        }
    }

    /**
     * Gets the style of the textarea.
     * 
     * @return  {Object}    style   style of the textarea
     */

    getStyle = () => {

        // Preparing

        let style = this._getStyle();
        
        // Adding style attributes
        
        style.marginBottom = (this.props.bottomgap || 0) + 16 + 'px';

        // Returns the style

        return style;
    }

    /**
     * Textarea renderer.
     */

    render() {

        // Setting default label position

        let labelposition = this.props.labelposition || Textarea.LABELPOSITION.LEFT;

        // Rendering

        return (

            /* Textarea container */

            <div className="textarea" style={this.getStyle()} title={this.props.tooltip}>
                <div className={`${this.props.label && labelposition === Textarea.LABELPOSITION.LEFT ? 'horizontal' : 'vertical'}`}>

                    {/* Label */}
                
                    { this.props.label && <div><Label text={this.props.label} topgap={labelposition === Textarea.LABELPOSITION.TOP ? 0 : 6} rightgap={this.props.labelwidth || labelposition === Textarea.LABELPOSITION.TOP ? 0 : 8} width={this.props.labelwidth} isrequired={this.props.isrequired} requiredmessage={this.props.requiredmessage} /></div>}
                    
                    <div>

                        {/* Textarea */}

                        <textarea  
                            ref={c => {this.input = c;}} 
                            disabled={this.props.disabled}
                            placeholder={this.props.placeholder}
                            className={`textarea-input ${this.state.error ? 'textarea-input-invalid' : ''} textarea-input-${this.props.disabled ? 'disabled' : 'enabled'}`}
                            maxLength={this.props.maxlength || 1000}
                            style={{ width: (this.props.width || 400) + 'px', height: (this.props.height || 160) + 'px', textTransform: this.props.touppercase ? 'uppercase' : this.props.tocapitalize ? 'capitalize' : 'none' }}
                            onFocus={(event) => {event.target.select();}}
                            onKeyUp={this._validateOnTyping}
                            onBlur={this._validateOnBlur}
                        ></textarea>

                        {/* Comment */}

                        { this.props.comment && <div className="textarea-comment">{this.props.comment}</div> }

                        {/* Error message */}

                        {this.state.error && (<div className={`textarea-error ${this.state.showerror ? 'textarea-error-show' : ''}`} style={{ top: (this.props.comment ? -9 : 4) + 'px'}}>
                            <div className="textarea-error-container">
                                <div className="textarea-error-content">{this.state.error}</div>
                            </div>
                        </div>)}
                    </div>
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/Textarea/Textarea.js